import { TranslateExcelOrder, TranslateExcelOrderBusiness } from '../translateExcelOrder';
import { OrderStatus } from './arrival';
import { AlterableHeaderService, type AlterableHeader } from './inputExcelInfo';

/**
 * 発注エクセル登録設定
 */
export type RegisterOrderExcelInfo = {
  id: string;
  name: string;
  headerLineNumber: number;
  rowMappingRules: OrderRowMappingRule[];
  duplicateResolution: DuplicateResolutionRule;
};

/**
 * 発注エクセル登録設定一覧
 */
export type RegisterOrderExcelInfos = RegisterOrderExcelInfo[];

/**
 * 発注エクセル1行に適用するマッピングルール
 */
export type OrderRowMappingRule = {
  id: string;
  name: string;
  priority: number;
  duplicatePriority: number | undefined;
  rowConditions: OrderMappingRuleCondition[];
  fieldMappings: OrderFieldMappings;
  orderStatusRules: ImportOrderStatusRule[];
  fallbackOrderStatus: OrderStatus;
};

/**
 * 発注エクセルでマッピングできる項目
 */
export type OrderMappingField = Extract<
  AlterableHeader,
  | 'WarehouseNumber'
  | 'Warehouse'
  | 'SupplierNumber'
  | 'SupplierName'
  | 'BusinessNumber'
  | 'BusinessName'
  | 'Package'
  | 'OrderNumber'
  | 'OrderIssuedNumber'
  | 'CreatedOrderDate'
  | 'EstimatedTimeOfArrival'
  | 'OrderAmount'
>;

/**
 * 発注エクセルの項目別マッピング一覧
 */
export type OrderFieldMappings = Record<OrderMappingField, OrderFieldMapping | undefined>;

/** 発注フィールドマッピングの入力エラー */
export type OrderFieldMappingValidateError = 'notEmpty' | 'none';

/** 発注フィールドマッピングのデータタイプ */
export type OrderFieldMappingDataType = 'string' | 'number' | 'date';

/** 発注フィールドマッピングの共通項目 */
type OrderFieldMappingBase = {
  unused: boolean | undefined;
  validateError: OrderFieldMappingValidateError | undefined;
  dataType: OrderFieldMappingDataType;
};

/**
 * 発注エクセルの1項目を解決するマッピング式
 */
export type OrderFieldMapping = OrderColumnFieldMapping | OrderLiteralFieldMapping | OrderConcatFieldMapping;

/**
 * Excel列から値を取得するマッピング
 */
export type OrderColumnFieldMapping = {
  type: 'column';
  column: string;
} & OrderFieldMappingBase;

/**
 * 固定値を返すマッピング
 */
export type OrderLiteralFieldMapping = {
  type: 'literal';
  value: string;
} & OrderFieldMappingBase;

/**
 * 複数のマッピング結果を連結するマッピング
 */
export type OrderConcatFieldMapping = {
  type: 'concat';
  parts: OrderFieldMapping[];
} & OrderFieldMappingBase;

/** 条件演算子 */
export const ConditionOperators = [
  'is_empty',
  'is_not_empty',
  'equals',
  'not_equals',
  'gt',
  'gte',
  'lt',
  'lte',
  'contains',
  'not_contains',
] as const;

/** 条件演算子の型 */
export type ConditionOperator = (typeof ConditionOperators)[number];

/** 条件値の入力種別 */
export const ConditionValueTypes = ['text', 'number', 'date'] as const;

/** 条件値の入力種別の型 */
export type ConditionValueType = (typeof ConditionValueTypes)[number] | 'none';

/**
 * 発注エクセル1行にマッピングルールを適用する条件
 */
export type OrderMappingRuleCondition = {
  id: string;
  header: string;
  operator: ConditionOperator;
  valueType: ConditionValueType;
  value: string | number | null;
  validateError: ImportOrderStatusConditionValidateError;
};

/** 発注ステータス取得条件の入力エラー */
export type ImportOrderStatusConditionValidateError = {
  header: 'notEmpty' | 'none';
  value: 'notEmpty' | 'none';
};

/** 発注ステータスの取得条件 */
export type ImportOrderStatusCondition = {
  id: string;
  header: string;
  operator: ConditionOperator;
  valueType: ConditionValueType;
  value: string | number | null;
  validateError: ImportOrderStatusConditionValidateError;
};

/** 発注ステータスの取得ルール */
export type ImportOrderStatusRule = {
  id: string;
  conditions: ImportOrderStatusCondition[];
  result: OrderStatus;
};

/**
 * 同一発注とみなされる行が複数ある場合の解決方法
 */
export type DuplicateResolutionRule =
  | {
      type: 'keepAll';
    }
  | {
      type: 'preferHigherPriority';
      keyMappings: OrderFieldMapping[];
    };

/**
 * 発注エクセル行を変換した後、重複解決前に保持する候補
 */
export type ParsedOrderCandidate = {
  orderBusiness: TranslateExcelOrderBusiness;
  order: TranslateExcelOrder;
  ruleId: string;
  duplicatePriority: number;
  duplicateKey: string | undefined;
};

/**
 * 発注読み込み設定用のIDを作成する
 * @param prefix - IDの接頭辞
 */
export const createId = (prefix: string): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const randomId = Array.from({ length: 16 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  return `${prefix}${randomId}`;
};

/**
 * 発注用の初期読み込み設定を作成する
 * @param name - 設定名
 * @param headerLineNumber - ヘッダー行番号
 */
export const createDefaultOrderExcelInfo = (name: string, headerLineNumber = 0): RegisterOrderExcelInfo => ({
  id: createId('OM'),
  name,
  headerLineNumber,
  rowMappingRules: [
    {
      id: createId('OMR'),
      name: 'default',
      priority: 0,
      duplicatePriority: undefined,
      rowConditions: [],
      fieldMappings: (AlterableHeaderService.orderAlterableHeaderList as OrderMappingField[]).reduce<OrderFieldMappings>(
        (fieldMappings, field) => {
          fieldMappings[field] = {
            dataType: getDataTypeByField(field),
            type: 'column',
            column: '',
            unused: false,
            validateError: 'none',
          };
          return fieldMappings;
        },
        {} as OrderFieldMappings
      ),
      orderStatusRules: [],
      fallbackOrderStatus: OrderStatus.ApprovedAwaitingDeliveryDate,
    },
  ],
  duplicateResolution: {
    type: 'keepAll',
  },
});

/**
 * フィールドのデータタイプを取得する
 * @param field - フィールド
 */
export const getDataTypeByField = (field: OrderMappingField): OrderFieldMappingDataType => {
  switch (field) {
    case 'Package':
    case 'OrderAmount':
      return 'number';
    case 'CreatedOrderDate':
    case 'EstimatedTimeOfArrival':
      return 'date';
    default:
      return 'string';
  }
};

/**
 * 発注フィールドマッピングにデータタイプを補完する
 * @param field - フィールド
 * @param mapping - 発注フィールドマッピング
 */
export const normalizeOrderFieldMappingDataType = (
  field: OrderMappingField,
  mapping?: OrderFieldMapping
): OrderFieldMapping | undefined => {
  if (!mapping) return undefined;

  const dataType = mapping.dataType ?? getDataTypeByField(field);
  if (mapping.type !== 'concat') return { ...mapping, dataType };

  return {
    ...mapping,
    dataType,
    parts: mapping.parts.map((part) => normalizeOrderFieldMappingDataType(field, part) as OrderFieldMapping),
  };
};

/**
 * 保存済み発注エクセル登録設定にデータタイプを補完する
 * @param orderExcelInfos - 発注エクセル登録設定一覧
 */
export const normalizeRegisterOrderExcelInfoDataTypes = (orderExcelInfos: RegisterOrderExcelInfos): RegisterOrderExcelInfos =>
  orderExcelInfos.map((orderExcelInfo) => ({
    ...orderExcelInfo,
    rowMappingRules: orderExcelInfo.rowMappingRules.map((rowMappingRule) => ({
      ...rowMappingRule,
      fieldMappings: (AlterableHeaderService.orderAlterableHeaderList as OrderMappingField[]).reduce<OrderFieldMappings>(
        (fieldMappings, field) => {
          fieldMappings[field] = normalizeOrderFieldMappingDataType(field, rowMappingRule.fieldMappings[field]);
          return fieldMappings;
        },
        { ...rowMappingRule.fieldMappings }
      ),
    })),
  }));
